import { useMemo } from 'react';
import { Feature } from '@weave/schema-gen-ts/dist/shared/feature/location_feature.pb';
import { PlatformLocationFeatureV1 } from '@frontend/api-platform-location-feature';
import { useAppScopeStore } from '@frontend/scope';
import { LocationCreditData } from '../types';
import { getAIRCreditUsagePercent, getAIRCreditUsageVariant } from '../utils';

export const useLocationAirCreditData = (locationIds: string[], enabled = true) => {
  const { getLocationName } = useAppScopeStore();

  const { data, isLoading } = PlatformLocationFeatureV1.Queries.useGetLocationFeatureCreditDetailsQuery({
    request: {
      locationIds,
      // TODO: Milind - Update this logic when api supports credit for new WEAVE_VOICE_AIR as well
      featureEnum: Feature.TRUELARK_AI_VOICE,
    },
    options: {
      enabled: enabled && locationIds.length > 0,
      cacheTime: 0,
      staleTime: 0,
    },
  });

  const locationCreditDataList = useMemo<LocationCreditData[]>(() => {
    const locationCreditDetails = data?.locationCreditDetails ?? {};
    return Object.entries(locationCreditDetails).map(([locationId, details]) => {
      const totalCredits = details.credits?.totalCredits ?? 0;
      const consumedCredits = details.credits?.consumedCredits ?? 0;
      const usagePercent = getAIRCreditUsagePercent(consumedCredits, totalCredits);

      return {
        locationId,
        locationName: getLocationName(locationId) || locationId,
        creditExhaustedAt: details.creditExhaustedAt,
        consumedCredits,
        totalCredits,
        usagePercent,
        variant: getAIRCreditUsageVariant(usagePercent),
        billingEndDate: details.billingCycle?.billingEndDate,
        forecastedEndDate: details.billingCycle?.forecastedEndDate,
      };
    });
  }, [data, getLocationName]);

  const exhaustedLocations = useMemo(
    () => locationCreditDataList.filter(({ variant }) => variant === 'exhausted'),
    [locationCreditDataList]
  );

  const criticalLocations = useMemo(
    () => locationCreditDataList.filter(({ variant }) => variant === 'critical'),
    [locationCreditDataList]
  );

  const warningLocations = useMemo(
    () => locationCreditDataList.filter(({ variant }) => variant === 'warning'),
    [locationCreditDataList]
  );

  return {
    locationCreditDataList,
    exhaustedLocations,
    criticalLocations,
    warningLocations,
    isLoading,
  };
};
